import {
  assetViewConstraints,
  EventEmitter,
  getTranslatedText,
  Loader,
  NoDataCard,
  Pagination,
  preFormatForm,
  reactSelectInlineError,
  TEXT_LENGTH,
  validateForm
} from '@nile/components/src';
import {
  GET_ENTITY_PROPERTIES,
  GET_RULEBUILDER_OPERATORS,
  RMMClient,
  useCachedQuery
} from '@nile/graphql/src';
import envConfig from '@nile/home/src/Config/env';
import { ALL_ASSET_TYPES, ASSET_TYPES } from '@nile/rmm/src/constants';
import { AssetViewForm } from '@nile/rmm/src/modules/asset-management/components/pages/asset-view-create-edit/AssetViewSheet';
import AssetTypeCell from '@nile/rmm/src/modules/asset-management/components/sub-pages/asset-type-cell';
import AssetViewRuleBuilder from '@nile/rmm/src/modules/asset-management/components/sub-pages/asset-view-rule-builder/RuleBuilder';
import {
  ADD_ASSET_VIEW,
  GET_ASSET_VIEW_META_INFO,
  GET_MONITORED_ASSETS,
  GET_MONITORED_ASSETS_IN_CONTRACT,
  GET_VIEW,
  UPDATE_ASSET_VIEW
} from '@nile/rmm/src/modules/asset-management/Graphql/asset';
import { SVGIcon } from '@superops/super-icons';
import { Button, Dropdown, TextField } from '@superops/ui-design-system';
import { isEmpty, pick } from 'lodash';
import React, {
  forwardRef,
  MutableRefObject,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
  useState
} from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ServiceItemSheetContext } from './NewServiceItemSheet';
import { GET_ALL_ASSETS } from '@nile/rmm/src/modules/asset-management/Graphql/asset';

const ITEMS_PER_PAGE = 50;

const PaginationButton = styled.div`
  cursor: ${(props) => (props.isDisabled ? 'not-allowed' : 'pointer')};
  background: #fff;
  width: 34px;
  height: 34px;
  border-radius: 4px;
  padding: 5px;
  border: 1px solid #dbdfe3;
  opacity: ${(props) => (props.isDisabled ? '0.4' : '1')};
`;

const AssetViewWrap = styled.div`
  .form {
    width: 70%;
    padding-right: 20px;
    overflow: auto;

    .run-condition {
      margin-top: 40px;
    }
  }

  .asset-preview {
    width: 30%;
  }
`;

const PreviewAssetsWrap = styled.div`
  background: #f5f5fd;
  padding: 15px 15px 0 15px;

  & > *:last-child {
    background: transparent;
    margin-bottom: 20px;
  }

  ${(props) => {
    if (props.justifyCenter) {
      return `
  justify-content: center;`;
    }
  }}

  .placeholder {
    font-size: 14px;
    font-weight: 400;
    color: #626265;
    margin-top: 80px;
    width: 90%;
    text-align: center;
  }
`;

interface IListView {
  id: string;
  name: string;
  module?: string;
  viewCondition: any;
  customView?: boolean;
  shared: boolean;
  systemViewProperty?: string;
  viewSubType: string;
}

interface IAssetView {
  onSubmit: (data: IListView) => Promise<Partial<IListView>>;
  data?: IListView;
  forwardedRef: MutableRefObject<{ validate: () => Promise<any> }>;
}

function AssetView({ onSubmit, data, forwardedRef }: IAssetView) {
  const intl = useIntl();

  const overrideData = { /* customView: true, */ shared: true };

  const { loading: operatorsLoading, data: allOperators } = useCachedQuery(
    GET_RULEBUILDER_OPERATORS
  );

  const { loading: entitiesLoading, data: allEntities } = useCachedQuery(
    GET_ENTITY_PROPERTIES
  );

  const {
    loading: assetListViewMetaLoading,
    data: assetViewMeta
  } = useCachedQuery(GET_ASSET_VIEW_META_INFO, {
    client: RMMClient
  });

  const [assetViewData, setAssetViewData] = useReducer(
    (s, n) => ({ ...s, ...n, ...overrideData }),
    { viewSubType: 'WORKSTATION_ASSET', ...data }
  );

  useEffect(() => {
    setAssetViewData(data);
  }, [data]);

  const [errors, setErrors] = useState({});

  const context = useContext(ServiceItemSheetContext);
  function validate() {
    const { name, viewSubType, viewCondition } = assetViewData;
    const validationErrors = validateForm(
      { ...preFormatForm({ name, viewSubType }), viewCondition },
      assetViewConstraints
    );

    setErrors(validationErrors || {});
    return validationErrors;
  }

  function handleValidateAndSubmit() {
    const _errors = validate();
    if (isEmpty(_errors)) {
      return onSubmit(assetViewData);
    }

    return Promise.reject();
  }

  useImperativeHandle(forwardedRef, () => ({
    validate: handleValidateAndSubmit
  }));

  const previewAssetsRef = useRef(null);

  if (entitiesLoading || operatorsLoading || assetListViewMetaLoading)
    return <Loader />;

  return (
    <AssetViewWrap className="flex ch-20">
      <div className="form">
        <AssetViewForm
          renderTextField={
            <TextField
              placeholder={getTranslatedText(
                intl,
                'rmm.assetView.assetViewName.placeholder'
              )}
              value={assetViewData.name}
              onChange={(name) => setAssetViewData({ name })}
              error={errors?.name}
              maxLength={TEXT_LENGTH.size_3}
            />
          }
          dropdownLabel="Asset types"
          renderDropdown={({ options, loading }) => (
            <Dropdown
              isLoading={loading}
              value={
                options?.find(
                  (el) => el.value === assetViewData?.viewSubType
                ) || []
              }
              // isDisabled={!isEmpty(data)}
              options={options || []}
              classNamePrefix="value_selector"
              onChange={(option) => {
                setAssetViewData({
                  viewSubType: option.value,
                  viewCondition: { joinOperator: 'AND', operands: [] }
                });
              }}
              menuPlacement="auto"
              placeholder={'Select asset Type'} // TODO: Fix
            />
          )}
          ruleBuilderHeader={getTranslatedText(
            intl,
            'rmm.assetView.ruleBuilder.header'
          )}
          getMonitoredAssetsMetaInfoQueryVariable={{
            viewType: isEmpty(assetViewData.viewSubType)
              ? 'WORKSTATION_ASSET'
              : assetViewData.viewSubType,
            version: 1,
            viewModule: 'CONTRACT_ASSET_VIEW'
          }}
          renderAssetViewRuleBuilder={
            <AssetViewRuleBuilder
              meta={assetViewMeta.getAssetViewConditionMetaInfo}
              allEntities={allEntities}
              allOperators={allOperators}
              query={
                isEmpty(assetViewData?.viewCondition)
                  ? null
                  : assetViewData.viewCondition
              }
              errors={errors}
              context={{
                updateAutomationRule: (viewCondition) =>
                  setAssetViewData({ viewCondition })
              }}
              intl={intl}
              currentModule={ASSET_TYPES?.find(
                (el) => el.name === assetViewData?.viewSubType
              )}
            />
          }
          error={
            typeof errors?.['viewCondition.operands']?.[0] === 'string' &&
            reactSelectInlineError(errors?.['viewCondition.operands'])
          }
        />
        {!context?.isNewContractTemplate && (
          <div className="run-condition">
            <Button
              icon={<SVGIcon name="EyeVisible" />}
              onClick={() => {
                const _errors = validate();
                if (isEmpty(_errors)) {
                  previewAssetsRef.current.triggerAssetPreview();
                }
              }}
              disabled={
                !assetViewData?.viewCondition?.operands.length ||
                !isEmpty(pick(errors, ['viewCondition', 'viewSubType']))
              }
            >
              <FormattedMessage id="admin.serviceCatalog.assetView.runCondition" />
            </Button>
          </div>
        )}
      </div>
      {!context?.isNewContractTemplate && (
        <div className="asset-preview">
          <PreviewAssets
            ref={previewAssetsRef}
            viewSubType={assetViewData?.viewSubType}
            viewCondition={assetViewData?.viewCondition}
          />
        </div>
      )}
    </AssetViewWrap>
  );
}

const PreviewAssets = forwardRef(function PreviewAssets(
  { viewSubType, viewCondition }: { viewSubType: string; viewCondition: any },
  ref: MutableRefObject<{
    triggerAssetPreview: () => void;
  }>
) {
  const isAllAssets =
    viewSubType === ALL_ASSET_TYPES.ALL_ASSETS ||
    viewSubType === ALL_ASSET_TYPES.ALL_NON_MONITORED_ASSET;

  const [
    refetchAllAssets,
    { data: allAssetsData, loading: allAssetsLoading }
  ] = useLazyQuery(GET_ALL_ASSETS, {
    client: RMMClient,
    fetchPolicy: 'network-only'
  });

  const [
    refetchMonitoredAssets,
    { data: monitoredAssetsData, loading: monitoredAssetsLoading }
  ] = useLazyQuery(GET_MONITORED_ASSETS_IN_CONTRACT, {
    client: RMMClient,
    fetchPolicy: 'network-only'
  });

  const context = useContext(ServiceItemSheetContext);

  const assetsData = isAllAssets
    ? allAssetsData?.getAllAssets
    : monitoredAssetsData?.getMonitoredAssets;

  const assetsList = assetsData?.assets;

  const listInfo = assetsData?.listInfo;

  const loading = monitoredAssetsLoading || allAssetsLoading;

  useEffect(() => {
    context.setTotalAssetsInPreview(listInfo?.totalCount);
  }, [listInfo]);

  const condition = useMemo(() => {
    let sites: Array<string> | undefined;
    if (context?.isSiteSpecificEnabled && context?.serviceItem.selectedSites) {
      sites = context.serviceItem.selectedSites.reduce((acc, site) => {
        if (site.id !== 'ALL_SITES') {
          acc.push(site.id);
        }
        return acc;
      }, []);
    }

    const operands = [
      {
        attribute: 'client.accountId',
        operator: 'is',
        value: context?.clientId
      }
    ];

    if (sites) {
      operands.push({
        attribute: 'site.id',
        operator: 'includes',
        value: sites
      });
    }

    return {
      ...viewCondition,
      operands: viewCondition ? [...viewCondition.operands, ...operands] : []
    };
  }, [
    viewCondition,
    context?.clientId,
    context?.serviceItem,
    context?.isSiteSpecificEnabled,
    context?.serviceItem.selectedSites
  ]);

  function triggerAssetPreview(selectedPage?: number) {
    if (loading) return;
    (isAllAssets ? refetchAllAssets : refetchMonitoredAssets)({
      variables: {
        listInfo: {
          page: selectedPage ?? listInfo?.page ?? 1,
          pageSize: ITEMS_PER_PAGE,
          condition,
          sort: [
            {
              attribute: 'name',
              order: 'ASC'
            }
          ]
        },
        viewType: viewSubType
      }
    });
  }

  useImperativeHandle(ref, () => ({
    triggerAssetPreview: () => triggerAssetPreview()
  }));

  function onPageChange(pageNumber) {
    triggerAssetPreview(pageNumber);
  }

  return (
    <PreviewAssetsWrap
      className="flex col center fhp"
      // isCenter={!(monitoredAssetsList && monitoredAssetsList.length > 0)}
      justifyCenter={loading}
    >
      {(() => {
        if (loading) {
          return <Loader />;
        }

        if (assetsData === undefined) {
          return (
            <div className="placeholder">
              <FormattedMessage id="admin.serviceCatalog.assetView.previewPlaceholder" />
            </div>
          );
        }

        if (assetsList && assetsList.length === 0) {
          return (
            <NoDataCard
              imageURL={`url(${envConfig.imageRoot}svg/documents-empty-state.svg)`}
              text={
                <FormattedMessage id="admin.serviceCatalog.assetView.preview.noData" />
              }
              type="secondary"
            />
          );
        }

        return (
          <>
            <div className="flex col g20 scroll pb20 fhp fw">
              {assetsList?.map((asset) => {
                return (
                  <AssetTypeCell
                    cell={{
                      row: { original: asset, value: asset.name },
                      value: asset.name
                    }}
                  />
                );
              })}
            </div>
            <Pagination
              onPageChange={onPageChange}
              rowCount={ITEMS_PER_PAGE}
              recordCount={listInfo?.totalCount}
              pages={
                listInfo
                  ? Math.ceil(listInfo.totalCount / listInfo.pageSize)
                  : 0
              }
              page={listInfo?.page || 0}
              setRowCount={() => {}}
              dynamicRowCount={false}
              isClientPagination={false}
              renderPagination={<CustomPagination />}
            />
          </>
        );
      })()}
    </PreviewAssetsWrap>
  );
});

function CreateAssetView(props: { forwardedRef: IAssetView['forwardedRef'] }) {
  const context = useContext(ServiceItemSheetContext);
  const [createView] = useMutation(ADD_ASSET_VIEW, {
    client: RMMClient,
    onCompleted(resp) {
      // EventEmitter.dispatch('refetchGetViews', {
      //   operation: 'create',
      //   id: resp?.addAssetView?.id
      // });
      context?.setSelectedAssetView(resp?.addAssetView);
    }
  });

  return (
    <AssetView
      {...props}
      onSubmit={(data) => {
        return createView({
          variables: { input: { ...data, module: 'CONTRACT_ASSET_VIEW' } }
        });
      }}
    />
  );
}

function EditAssetView({
  id,
  ...rest
}: { id: string } & Omit<IAssetView, 'onSubmit'>) {
  const [updateView] = useMutation(UPDATE_ASSET_VIEW, {
    client: RMMClient,
    onCompleted(resp) {
      // EventEmitter.dispatch('refetchGetViews', {
      //   operation: 'update',
      //   id: resp?.updateAssetView?.id
      // });
    }
  });

  const { data: assetViewData, loading } = useQuery(GET_VIEW, {
    client: RMMClient,
    variables: {
      viewID: id,
      module: 'CONTRACT_ASSET_VIEW'
    },
    fetchPolicy: 'network-only'
  });

  if (loading) return <Loader />;
  return (
    <AssetView
      {...rest}
      onSubmit={(data) => {
        const { id, ...restData } = data;
        return updateView({
          variables: {
            input: { ...restData, module: 'CONTRACT_ASSET_VIEW' },
            viewID: id
          }
        });
      }}
      data={assetViewData?.getView}
    />
  );
}

const AssetViewDecider = forwardRef(
  (
    {
      isEdit,
      id,
      ...rest
    }: { isEdit: boolean; id: string } & Omit<IAssetView, 'onSubmit'>,
    ref: MutableRefObject<{ validate: () => Promise<any> }>
  ) => {
    if (isEdit) {
      return <EditAssetView {...rest} id={id} forwardedRef={ref} />;
    }

    return <CreateAssetView {...rest} forwardedRef={ref} />;
  }
);

export function AssetViewToolbar({ isEdit, onBack }) {
  return (
    <div className="flex center">
      <Button icon={<SVGIcon name="BackArrow" />} onClick={onBack} />
      <span className="ml-10">
        <FormattedMessage
          id={
            isEdit
              ? 'admin.serviceCatalog.assetView.header.editCondition'
              : 'admin.serviceCatalog.assetView.header.newCondition'
          }
        />
      </span>
    </div>
  );
}

function CustomPagination({
  previousPage,
  isPreviousDisabled,
  nextPage,
  isNextDisabled,
  totalCount,
  activePage,
  totalPages
}) {
  return (
    <div className="flex center jctr">
      <PaginationButton onClick={previousPage} isDisabled={isPreviousDisabled}>
        <SVGIcon name="ArrowLeft" />
      </PaginationButton>

      <span className="px15 f-14 text-medium">
        {' '}
        {`Page ${activePage} of ${totalPages}`}
      </span>

      <PaginationButton onClick={nextPage} isDisabled={isNextDisabled}>
        <SVGIcon name="ArrowRight" />
      </PaginationButton>
    </div>
  );
}

export default AssetViewDecider;
